'use client';

import { useState, useCallback, useEffect } from 'react';
import Modal from 'react-modal';
import Link from 'next/link';
import Image from 'next/image';
import { signIn } from 'next-auth/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@awesome.me/kit-94e593bf23/icons/classic/light';
import { faEye, faEyeSlash } from '@awesome.me/kit-94e593bf23/icons/sharp/light';
import { faSpinnerThird } from '@awesome.me/kit-94e593bf23/icons/classic/light';
import FormError from '@/app/_components/FormError';
import RecaptchaDisclaimer from '@/app/_components/RecaptchaDisclaimer';
import { useAppContext } from '@/app/_lib/_context';
import { useReCaptcha } from 'next-recaptcha-v3';
import { trackLogin } from '@/app/_lib/analytics/ga4';
const modalImages = [{
  desktop: '/images/modal-images/hive-popup-diptych-general-finn-juhl-chieftain.jpg',
  mobile: '/images/modal-images/mobile/hive-popup-diptych-mobile-general-finn-juhl-chieftain.jpg'
}, {
  desktop: '/images/modal-images/hive-popup-diptych-general-flos-luminator.jpg',
  mobile: '/images/modal-images/mobile/hive-popup-diptych-mobile-general-flos-luminator.jpg'
}, {
  desktop: '/images/modal-images/hive-popup-diptych-general-kartell-pilastro.jpg',
  mobile: '/images/modal-images/mobile/hive-popup-diptych-mobile-general-kartell-pilastro.jpg'
}, {
  desktop: '/images/modal-images/hive-popup-diptych-general-knoll-platner.jpg',
  mobile: '/images/modal-images/mobile/hive-popup-diptych-mobile-general-knoll-platner.jpg'
}];
const signInSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Please enter a valid email'),
  password: z.string().min(1, 'Password is required')
});
const SignInModal = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const {
    closeSignInModal
  } = useAppContext();
  const {
    executeRecaptcha
  } = useReCaptcha();
  const [modalImage, setModalImage] = useState(null);
  useEffect(() => {
    Modal.setAppElement('#hivenext');
  }, []);
  const [serverError, setServerError] = useState(null);
  const [fadeOutMessage, setFadeOutMessage] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: {
      errors,
      isSubmitting
    }
  } = useForm({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: '',
      password: ''
    }
  });

  // Reset form state when modal opens
  useEffect(() => {
    if (isOpen) {
      reset();
      setServerError(null);
      setFadeOutMessage(false);
      setModalImage(modalImages[Math.floor(Math.random() * modalImages.length)]);
    }
  }, [isOpen, reset]);
  const onSubmit = async data => {
    // Fade out existing error message if present
    if (serverError) {
      setFadeOutMessage(true);
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    // Get reCAPTCHA token
    const recaptchaToken = await executeRecaptcha('login');
    if (!recaptchaToken) {
      setServerError('Security verification failed. Please refresh the page and try again.');
      setFadeOutMessage(false);
      return;
    }

    // Use NextAuth signIn with credentials provider
    // Wrap in timeout to prevent infinite hang
    try {
      const signInPromise = signIn('credentials', {
        email: data.email,
        password: data.password,
        recaptchaToken: recaptchaToken,
        redirect: false
      });
      const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('SIGNIN_TIMEOUT')), 30000));
      const result = await Promise.race([signInPromise, timeoutPromise]);
      if (result?.ok) {
        trackLogin();
        // Close modal - session change triggers context update automatically
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setServerError('Invalid email or password. Please try again.');
        setFadeOutMessage(false);
      }
    } catch (error) {
      if (error.message === 'SIGNIN_TIMEOUT') {
        setServerError('Sign in is taking too long. Please refresh and try again.');
      } else {
        setServerError('An error occurred. Please try again.');
      }
      setFadeOutMessage(false);
    }
  };
  const togglePasswordVisibility = useCallback(() => {
    setPasswordVisible(prev => !prev);
  }, []);
  const handleGoogleSignIn = async e => {
    e.preventDefault();
    e.stopPropagation();
    setIsGoogleLoading(true);
    try {
      await signIn('google', {
        callbackUrl: '/account'
      });
    } catch (error_0) {
      console.error('Google sign-in error:', error_0);
      setIsGoogleLoading(false);
    }
  };
  return <Modal isOpen={isOpen} onRequestClose={onClose} contentLabel="Sign In" overlayClassName="fixed inset-0 bg-modal-overlay z-[1000] flex items-center justify-center" className="relative bg-white rounded-lg shadow-lg w-[calc(100%-2rem)] max-w-[400px] md:max-w-[800px] max-h-[calc(100dvh-2rem)] mx-4 outline-none overflow-y-auto" bodyOpenClassName="ReactModal__Body--open" htmlOpenClassName="ReactModal__Html--open">
            <div className="flex flex-col md:flex-row min-h-0">
                {/* Left Side - Image */}
                <div className="w-full md:w-1/2 order-2 md:order-1 md:self-stretch">
                    {modalImage && <>
                            <Image src={modalImage.mobile} alt="Sign in to hive" width={400} height={300} quality={90} sizes="100vw" className="w-full h-auto block md:hidden" />
                            <Image src={modalImage.desktop} alt="Sign in to hive" width={400} height={600} quality={90} sizes="50vw" className="w-full h-full object-cover hidden md:block" />
                        </>}
                </div>

                {/* Right Side - Form */}
                <div className="w-full md:w-1/2 p-6 relative order-1 md:order-2">
                    {/* Close button */}
                    <button type="button" onClick={onClose} className="absolute top-4 right-4 text-light hover:text-dark transition-colors" aria-label="Close modal">
                        <FontAwesomeIcon icon={faXmark} className="text-2xl" />
                    </button>

                    <h2 className="text-xl font-light mb-6">Sign In</h2>

                <form onSubmit={handleSubmit(onSubmit)}>
                    {serverError && <div role="alert" aria-live="assertive" className={`text-red font-medium p-3 border-l-4 border-red mb-4 transition-opacity duration-300 ${fadeOutMessage ? 'opacity-0' : 'opacity-100'}`}>
                            {serverError}
                        </div>}

                    <div className="form-group mb-4">
                        <input id="modal-email" type="email" autoComplete="username" className={`form-input peer ${errors.email ? 'border-red' : ''}`} placeholder=" " aria-invalid={!!errors.email} aria-describedby={errors.email ? "modal-email-error" : undefined} {...register('email')} />
                        <label htmlFor="modal-email" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                            Email <span className="text-primary">*</span>
                        </label>
                        {errors.email && <FormError id="modal-email-error">{errors.email.message}</FormError>}
                    </div>

                    <div className="form-group mb-4">
                        <input type={passwordVisible ? 'text' : 'password'} id="modal-password" autoComplete="current-password" className={`form-input peer pr-8 ${errors.password ? 'border-red' : ''}`} placeholder=" " aria-invalid={!!errors.password} aria-describedby={errors.password ? "modal-password-error" : undefined} {...register('password')} />
                        <label htmlFor="modal-password" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                            Password <span className="text-primary">*</span>
                        </label>
                        <button type="button" onClick={togglePasswordVisibility} aria-label={passwordVisible ? 'Hide password' : 'Show password'} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                            <FontAwesomeIcon icon={passwordVisible ? faEye : faEyeSlash} className="text-xs" aria-hidden="true" />
                        </button>
                        {errors.password && <FormError id="modal-password-error">{errors.password.message}</FormError>}
                    </div>

                    <div className="flex items-center gap-4 mb-4">
                        <button type="submit" disabled={isSubmitting} className={`btn btn-primary flex items-center justify-center px-6 ${isSubmitting ? 'opacity-75' : ''}`}>
                            {isSubmitting ? <>
                                    <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin mr-2" />
                                    Signing in...
                                </> : 'Sign In'}
                        </button>
                        <Link href="/account/reset-password" onClick={onClose} className="text-sm text-ink-light hover:text-primary transition-colors whitespace-nowrap">
                            Reset Password
                        </Link>
                    </div>
                </form>

                {/* OAuth Sign In Options */}
                <div className="relative my-5">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white text-gray-500">Or continue with</span>
                    </div>
                </div>

                <button onClick={handleGoogleSignIn} disabled={isGoogleLoading} type="button" className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-10 rounded text-sm font-medium text-ink bg-white hover:border-primary hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    {isGoogleLoading ? <>
                            <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin" />
                            Connecting to Google...
                        </> : <>
                            <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                            </svg>
                            Sign in with Google
                        </>}
                </button>

                {/* Apple Sign In - Hidden until configured */}
                {false && <button type="button" className="w-full flex items-center justify-center gap-3 px-4 py-3 mt-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-white bg-black hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150">
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                        </svg>
                        Sign in with Apple
                    </button>}

                <div className="border-t border-gray-8 pt-4 mt-4">
                    <p className="text-xs text-ink-light text-center">
                        Don&apos;t have an account?{' '}
                        <Link href="/account/register" onClick={onClose} className="text-primary hover:underline">
                            Create Account
                        </Link>
                    </p>
                </div>

                <div className="mt-4">
                        <RecaptchaDisclaimer />
                    </div>
                </div>
            </div>
        </Modal>;
};
export default SignInModal;